import { Button } from '@lifeomic/chroma-react/components/Button';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Radio, RadioGroup } from '@lifeomic/chroma-react/components/Radio';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  KNOWN_IMPRESSION_TYPES,
  KnownImpressionTypes,
} from '../../../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import {
  useCreateTemplatingItemForm,
  useTemplatingItemTypeDisplay,
} from '../../hooks';
import { ImpressionTypeFilter } from '../../ImpressionTypeFilter';
import {
  Folder,
  TemplatingItemDetail,
  TemplatingItemSummary,
} from '../../models';

const messages = defineMessages({
  modalTitle: {
    id: 'ReportTemplates.CreateTemplatingItemModal.title',
    defaultMessage: 'Create Template in {folderPath}',
  },
  modalTitleCopy: {
    id: 'ReportTemplates.CreateTemplatingItemModal.titleCopy',
    defaultMessage: 'Copy {typeName}',
  },
  modalTitleRoot: {
    id: 'ReportTemplates.CreateTemplatingItemModal.titleRoot',
    defaultMessage: 'Create Template',
  },
  createNewSubfolder: {
    id: 'ReportTemplates.CreateTemplatingItemModal.createNewSubfolder',
    defaultMessage: 'Create new subfolder',
  },
  folderNameLabel: {
    id: 'ReportTemplates.CreateTemplatingItemModal.folderName',
    defaultMessage: 'Folder name',
  },
  templatingItemNameLabel: {
    id: 'ReportTemplates.CreateTemplatingItemModal.templatingItemName',
    defaultMessage: '{typeName} name',
  },
  createButton: {
    id: 'ReportTemplates.CreateTemplatingItemModal.create',
    defaultMessage: 'Create',
  },
  copyButton: {
    id: 'ReportTemplates.CreateTemplatingItemModal.copy',
    defaultMessage: 'Copy',
  },
  cancelButton: {
    id: 'ReportTemplates.CreateTemplatingItemModal.cancel',
    defaultMessage: 'Cancel',
  },
  compositeReportDefinitionCreationMessage: {
    id: 'ReportTemplates.CreateTemplatingItemModal.compositeReportDefinitionCreationMessage',
    defaultMessage:
      'After creating the composite report template, you will be able to add templates to the definition to define its sections.',
  },
  compositeReportDefinitionHelpMessage: {
    id: 'ReportTemplates.CreateTemplatingItemModal.compositeReportDefinitionHelpMessage',
    defaultMessage:
      'A composite report template is a group of templates used to create a report with multiple sections.',
  },
});

const useStyles = makeStyles(theme => ({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

type CreateTemplatingItemModalProps = {
  folder?: Folder;
  /** A templating item to copy when creating this templating item */
  templatingItem?: TemplatingItemSummary;
  isOpen: boolean;
  onSuccess?: (templatingItem: TemplatingItemDetail) => void;
  onDismiss: () => void;
};

export const CreateTemplatingItemModal: React.FC<
  CreateTemplatingItemModalProps
> = ({ folder, templatingItem, isOpen, onSuccess, onDismiss }) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();

  const {
    templatingItemTypeControl,
    templatingItemNameControl,
    folderNameControl,
    shouldCreateFolderControl,
    impressionTypeControl,
    onSubmit,
    hasError,
    reset,
  } = useCreateTemplatingItemForm({
    folder,
    templatingItem,
    onSuccess,
  });

  const handleDismiss = useCallback(() => {
    reset();
    onDismiss();
  }, [reset, onDismiss]);

  const isCopying = templatingItem !== undefined;

  const templatingItemTypeDisplay = useTemplatingItemTypeDisplay();
  const selectedTemplatingItemTypeDisplay =
    templatingItemTypeDisplay[templatingItemTypeControl.field.value];

  const modalTitleCreate = folder?.path
    ? formatMessage(messages.modalTitle, {
        folderPath: folder.path,
      })
    : formatMessage(messages.modalTitleRoot);

  const modalTitleCopy = formatMessage(messages.modalTitleCopy, {
    typeName: selectedTemplatingItemTypeDisplay,
  });

  const modalTitle = templatingItem ? modalTitleCopy : modalTitleCreate;

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size={1}
      title={modalTitle}
      actions={
        <>
          <Button variant="text" color="negative" onClick={handleDismiss}>
            {formatMessage(messages.cancelButton)}
          </Button>
          <Button onClick={() => onSubmit()} disabled={hasError}>
            {templatingItem
              ? formatMessage(messages.copyButton)
              : formatMessage(messages.createButton)}
          </Button>
        </>
      }
    >
      <div className={styles.modalContent}>
        {!isCopying && (
          <>
            <RadioGroup
              title="Template Type"
              value={templatingItemTypeControl.field.value}
              onChange={templatingItemTypeControl.field.onChange}
              direction="column"
              align="flex-start"
            >
              <Radio
                value="template"
                name="templatingItemType"
                label={templatingItemTypeDisplay.singleReportTemplate}
              />
              <Radio
                value="compositeReportDefinition"
                name="templatingItemType"
                label={templatingItemTypeDisplay.compositeReportDefinition}
                helpMessage={formatMessage(
                  messages.compositeReportDefinitionHelpMessage,
                )}
              />
            </RadioGroup>
            <Divider />
          </>
        )}
        <Checkbox
          label={formatMessage(messages.createNewSubfolder)}
          aria-label={formatMessage(messages.createNewSubfolder)}
          checked={shouldCreateFolderControl.field.value}
          onChange={shouldCreateFolderControl.field.onChange}
        />
        {shouldCreateFolderControl.field.value && (
          <TextField
            label={formatMessage(messages.folderNameLabel)}
            aria-label={formatMessage(messages.folderNameLabel)}
            {...folderNameControl.field}
            errorMessage={folderNameControl.fieldState.error?.message}
            hasError={!!folderNameControl.fieldState.error}
            fullWidth
          />
        )}
        <TextField
          label={formatMessage(messages.templatingItemNameLabel, {
            typeName: selectedTemplatingItemTypeDisplay,
          })}
          aria-label={formatMessage(messages.templatingItemNameLabel, {
            typeName: selectedTemplatingItemTypeDisplay,
          })}
          {...templatingItemNameControl.field}
          errorMessage={templatingItemNameControl.fieldState.error?.message}
          hasError={!!templatingItemNameControl.fieldState.error}
          fullWidth
        />
        {templatingItemTypeControl.field.value === 'template' && (
          <ImpressionTypeFilter
            impressionTypes={KNOWN_IMPRESSION_TYPES as KnownImpressionTypes[]}
            value={impressionTypeControl.field.value}
            onChange={impressionTypeControl.field.onChange}
            disabled={false}
            extraSections={[]}
            variant="form-field"
          />
        )}
        {templatingItemTypeControl.field.value ===
          'compositeReportDefinition' && (
          <Text size="caption">
            {formatMessage(messages.compositeReportDefinitionCreationMessage)}
          </Text>
        )}
      </div>
    </Modal>
  );
};
